import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { DocumentDto, DocumentOpenType } from '../../../../context/GlobalContext'
import { useFormikContext } from 'formik'
import { useSettings } from '../../../../hooks/useSettings'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { DocumentSelectionType } from '../../../../components/Dropdown/DocumentDropdown'
import { DocumentContext, DocumentIconNameMap, FolderType } from '../../../../config/documentConfig'
import { DocumentType } from '../../../../helpers/documentHelper'
import { FilePartyDto } from '../../../../config/filePartyConfig'
import { formatInputDocumentName, formatTemplateDocumentName } from '../../../../helpers/regexHelper'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../../../hooks/document/useDocumentGenerationHandler'
import { QueueStatus } from '../../../../hooks/useQueueStateHandler'
import { stealthClick } from '../../../../components/StealthLink'
import * as Yup from 'yup'
import DocumentCombobox, { DocumentComboboxProps } from '../../Combobox/DocumentCombobox'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FormikConfig } from 'formik/dist/types'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../../../components/Combobox/EnumMapCombobox'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import { IFileDto } from '../../../../config/fileConfig'

export const getOfficeDocumentDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        type: Yup.string().required(translate('file.pivot.document.create.template.required')),
        template: Yup.string().required(translate('file.pivot.document.create.template.required')),
        fileId: Yup.string().required(translate('file.pivot.document.create.template.required')),
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
        name: Yup.string()
            .required(translate('file.pivot.document.create.name.required'))
            .test('is-valid-name', translate('file.pivot.document.create.name.invalid'), value => !value.match(/^\./)),
    })
}

export type OfficeDocumentDialogBodyProps = {
    context: DocumentContext
    folders?: FolderDriveItem[]
    parentId?: string //selected folder
    documentRootId?: string //root folder to use to lazy load the document tree
    file?: IFileDto
    party?: FilePartyDto
    callback?: (driveItem: DriveItem) => void
}

export type OfficeDocumentDialogBodyValues = {
    type: DocumentType
    userId: string
    template: string
    fileId: string
    folderId: string
    name: string
    namePrefix?: string
    nameSuffix?: string
    pivotId?: string
}

export default function OfficeDocumentDialogBody(
    props: HammockFormDialogProps<OfficeDocumentDialogBodyValues, OfficeDocumentDialogBodyProps>,
): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { values, setFieldValue } = useFormikContext<OfficeDocumentDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as OfficeDocumentDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const templateOptions = useMemo<Map<DocumentDto, string>>(
        () =>
            new Map(
                (settings?.documents || [])
                    .filter(template => !template.context || template.context === dialogFormBodyProps.context)
                    .filter(template => !values.type || template.type === values.type)
                    .filter(template => !template.role || !dialogFormBodyProps.party || template.role === dialogFormBodyProps.party?.role)
                    .map(template => [template, template.label] as [DocumentDto, string]),
            ),
        [settings, settings?.documents],
    )
    const folders = useMemo(() => dialogFormBodyProps?.folders?.filter(item => item.itemType === 'branch'), [dialogFormBodyProps.folders])

    useEffect(() => {
        if (values.namePrefix && values.nameSuffix) {
            setFieldValue('name', formatInputDocumentName(values.namePrefix, values.nameSuffix))
        } else {
            setFieldValue('namePrefix', values.namePrefix ?? '')
            setFieldValue('nameSuffix', values.nameSuffix ?? '')
        }
    }, [values.namePrefix, values.nameSuffix])

    useEffect(() => {
        const template: DocumentDto = (templateOptions.keys() || []).find(template => template.uri === values.template)

        if (template) {
            setFieldValue('type', template.type)
            setFieldValue(
                'namePrefix',
                formatTemplateDocumentName(template.name, { file: dialogFormBodyProps.file, party: dialogFormBodyProps.party }),
            )
            setFieldValue('nameSuffix', DocumentIconNameMap.get(template.type))

            const destDefaultFolder =
                settings?.folders.find(folder => folder.type === template.folder) ||
                settings?.folders.find(folder => folder.type === FolderType.Correspondence)

            const targetFolder =
                folders?.find(folder => folder.name === destDefaultFolder.label) ||
                folders?.find(folder => folder.name.indexOf(destDefaultFolder.label) >= 0)
            setFieldValue('folderId', targetFolder?.id)
        }
    }, [values.template, folders])

    const submitOfficeRequest = useSubmitDocumentGenerationRequest<OfficeDocumentDialogBodyValues, DocumentGenerationStatusData>(
        DocumentGenerationType.Office,
        {
            [QueueStatus.Queued]: () => props.onDismiss(),
            [QueueStatus.Finished]: data => {
                dialogFormBodyProps?.callback?.(data.status.driveItem)
                stealthClick(
                    { id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                    axios,
                )
            },
        },
    )
    const submitOfficeV2Request = useSubmitDocumentGenerationRequest<OfficeDocumentDialogBodyValues, DocumentGenerationStatusData>(
        DocumentGenerationType.OfficeV2,
        {
            [QueueStatus.Queued]: () => props.onDismiss(),
            [QueueStatus.Finished]: data => {
                dialogFormBodyProps?.callback?.(data.status.driveItem)
                stealthClick(
                    { id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                    axios,
                )
            },
        },
    )

    const handleSubmit = useCallback<FormikConfig<OfficeDocumentDialogBodyValues>['onSubmit']>(
        values => {
            const template = settings.documents.find(t => t.uri === values.template)
            if (template?.version === 2) {
                submitOfficeV2Request(values, template.label)
            } else if (template) {
                submitOfficeRequest(values, template.label)
            }
        },
        [settings.documents, submitOfficeV2Request, submitOfficeRequest],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate(`file.pivot.document.create.${values.type?.toLowerCase() || 'standard'}`)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormikComboboxField<OfficeDocumentDialogBodyValues, EnumMapComboboxProps<DocumentDto>>
                        required
                        labelKey={'file.pivot.document.create.template.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'template'}
                        component={EnumMapCombobox}
                        componentProps={{
                            values: templateOptions,
                            placeholderPrefixKey: '',
                            placeholderKey: 'file.pivot.document.create.template.placeholder',
                            getEntityValue: entity => entity?.uri,
                        }}
                    />
                    <FormikComboboxField<OfficeDocumentDialogBodyValues, DocumentComboboxProps>
                        required
                        labelKey={'file.pivot.document.create.folderid.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'folderId'}
                        component={DocumentCombobox}
                        componentProps={{
                            selectionType: DocumentSelectionType.FolderOnly,
                            entities: folders,
                            documentRootId: dialogFormBodyProps.documentRootId,
                            onLazyLoaded: items => (dialogFormBodyProps.folders = items),
                        }}
                    />
                    <FormikInputField
                        required
                        labelKey={'file.pivot.document.create.name.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'namePrefix'}
                        componentProps={{
                            placeholder: translate('file.pivot.document.create.name.label'),
                            contentAfter: `.${values.nameSuffix}`,
                        }}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'}>
                    {translate('action.create.document')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
